/**
 * predictive/predictors.ts — pure prediction rules for Engine #8.
 *
 * Every predictor is a pure function of (snapshot, now): deterministic,
 * individually testable, and gracefully silent when its inputs are missing.
 * Each emitted Prediction carries confidence, data sources, supporting
 * factors, a time window, and an expiration — explanations are mandatory.
 */

import type {
  Prediction,
  PredictiveInputSnapshot,
  TimeSeriesPoint,
} from './models';

const MINUTE = 60 * 1000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

export type Predictor = (snapshot: PredictiveInputSnapshot, now: number) => Prediction[];

// ─── Shared helpers ────────────────────────────────────────────────────────────

/** Relative slope over a series: (last - first) / |first| — needs ≥3 points. */
export function trendSlope(series: TimeSeriesPoint[] | undefined): number | null {
  if (!series || series.length < 3) return null;
  const first = series[0].value;
  const last = series[series.length - 1].value;
  if (first === 0) return null;
  return (last - first) / Math.abs(first);
}

function clampConfidence(value: number): number {
  return Math.max(5, Math.min(99, Math.round(value)));
}

function hourStart(now: number, hour: number): number {
  const d = new Date(now);
  d.setHours(hour, 0, 0, 0);
  let at = d.getTime();
  if (at < now) at += DAY; // next occurrence
  return at;
}

// ─── Weather ───────────────────────────────────────────────────────────────────

const rainPredictor: Predictor = (s, now) => {
  const w = s.weather;
  if (!w || w.rainStartHour == null) return [];
  const startsAt = hourStart(now, w.rainStartHour);
  const minutes = Math.round((startsAt - now) / MINUTE);
  if (minutes > 6 * 60) return []; // too far out to be actionable
  const rainProbability = w.condition === 'storm' ? 95 : w.condition === 'rain' ? 92 : 70 + Math.min(20, w.humidityPct / 5);
  const out: Prediction[] = [
    {
      id: 'weather-rain-incoming',
      kind: 'prediction',
      category: 'weather',
      eventType: 'WeatherAlert',
      title: minutes <= 0 ? 'Rain is starting now.' : `Rain begins in ${minutes} minutes.`,
      detail: 'Expect wet conditions — plan indoor work and travel accordingly.',
      confidence: clampConfidence(rainProbability),
      dataSources: ['weather'],
      supportingFactors: [
        `Rain probability is ${Math.round(rainProbability)}%.`,
        `Humidity is at ${w.humidityPct}%.`,
      ],
      timeWindow: { start: startsAt, end: startsAt + 4 * HOUR },
      expiresAt: startsAt + 4 * HOUR,
      createdAt: now,
    },
  ];
  // Proactive recommendation — before the user searches.
  const workspace = (s.coworking ?? []).find((c) => c.hasAvailability);
  const factors = [
    `Rain probability is ${Math.round(rainProbability)}%.`,
    'Outdoor cafés typically become crowded after storms.',
  ];
  if (workspace) {
    factors.push(`${workspace.name} has availability.`);
    if (workspace.travelMinutes != null) factors.push(`Travel time is only ${workspace.travelMinutes} minutes.`);
  }
  out.push({
    id: 'weather-work-indoors',
    kind: 'recommendation',
    category: 'weather',
    eventType: 'PredictionCreated',
    title: 'Work indoors today due to rain.',
    detail: workspace ? `Head to ${workspace.name} before the rain starts.` : 'Pick an indoor workspace before the rain starts.',
    confidence: clampConfidence(rainProbability - 5),
    dataSources: workspace ? ['weather', 'coworking'] : ['weather'],
    supportingFactors: factors,
    timeWindow: { start: now, end: startsAt },
    expiresAt: startsAt + 2 * HOUR,
    createdAt: now,
  });
  return out;
};

// ─── Housing ───────────────────────────────────────────────────────────────────

const housingTrendPredictor: Predictor = (s, now) => {
  const slope = trendSlope(s.housingPrices);
  if (slope == null || Math.abs(slope) < 0.05) return [];
  const falling = slope < 0;
  const pct = Math.abs(Math.round(slope * 100));
  const out: Prediction[] = [
    {
      id: `housing-trend-${falling ? 'down' : 'up'}`,
      kind: 'prediction',
      category: 'housing',
      eventType: falling ? 'PriceDropDetected' : 'PredictionCreated',
      title: `Apartment prices are trending ${falling ? 'downward' : 'upward'}.`,
      detail: `Monthly rates have moved ${falling ? 'down' : 'up'} about ${pct}% recently.`,
      confidence: clampConfidence(55 + Math.min(30, pct * 2)),
      dataSources: ['housing'],
      supportingFactors: [
        `Listed prices changed ${falling ? '-' : '+'}${pct}% across ${s.housingPrices!.length} recent data points.`,
      ],
      timeWindow: { start: now, end: now + 14 * DAY },
      expiresAt: now + 7 * DAY,
      createdAt: now,
    },
  ];
  if (falling) {
    out.push({
      id: 'housing-book-now',
      kind: 'recommendation',
      category: 'housing',
      eventType: 'OpportunityDetected',
      title: 'Book housing now.',
      detail: 'Prices are soft — locking in a monthly rate now is favorable.',
      confidence: clampConfidence(50 + Math.min(30, pct * 2)),
      dataSources: ['housing'],
      supportingFactors: [`Apartment prices are down about ${pct}%.`, 'Downward trends in this market typically rebound within weeks.'],
      timeWindow: { start: now, end: now + 7 * DAY },
      expiresAt: now + 7 * DAY,
      createdAt: now,
    });
  }
  return out;
};

const cheaperApartmentPredictor: Predictor = (s, now) => {
  const listings = (s.housingListings ?? []).filter((l) => l.monthlyCostUsd != null);
  if (listings.length < 3) return [];
  const costs = listings.map((l) => l.monthlyCostUsd!).sort((a, b) => a - b);
  const median = costs[Math.floor(costs.length / 2)];
  const bargain = listings.find((l) => l.monthlyCostUsd! <= median * 0.8);
  if (!bargain) return [];
  return [{
    id: `housing-bargain-${bargain.id}`,
    kind: 'opportunity',
    category: 'housing',
    eventType: 'OpportunityDetected',
    title: 'Cheaper apartment available.',
    detail: `${bargain.title} is listed at $${bargain.monthlyCostUsd}/month.`,
    confidence: 80,
    dataSources: ['housing'],
    supportingFactors: [
      `$${bargain.monthlyCostUsd}/month is ${Math.round((1 - bargain.monthlyCostUsd! / median) * 100)}% below the local median of $${median}.`,
    ],
    timeWindow: { start: now, end: now + 3 * DAY },
    expiresAt: now + 3 * DAY,
    createdAt: now,
  }];
};

const hotelDiscountPredictor: Predictor = (s, now) => {
  const hotels = (s.housingListings ?? []).filter((l) => l.kind === 'hotel' && l.nightlyCostUsd != null);
  if (hotels.length < 3) return [];
  const costs = hotels.map((l) => l.nightlyCostUsd!).sort((a, b) => a - b);
  const median = costs[Math.floor(costs.length / 2)];
  const deal = hotels.find((l) => l.nightlyCostUsd! <= median * 0.7);
  if (!deal) return [];
  return [{
    id: `hotel-discount-${deal.id}`,
    kind: 'opportunity',
    category: 'hotels',
    eventType: 'PriceDropDetected',
    title: 'Hotel discount detected.',
    detail: `${deal.title} at $${deal.nightlyCostUsd}/night.`,
    confidence: 75,
    dataSources: ['housing'],
    supportingFactors: [`$${deal.nightlyCostUsd}/night vs a typical $${median}/night nearby.`],
    timeWindow: { start: now, end: now + 2 * DAY },
    expiresAt: now + 2 * DAY,
    createdAt: now,
  }];
};

// ─── Currency ──────────────────────────────────────────────────────────────────

const currencyPredictor: Predictor = (s, now) => {
  const slope = trendSlope(s.currencyRates);
  if (slope == null || Math.abs(slope) < 0.01) return [];
  const improving = slope > 0;
  const pct = Math.abs(Math.round(slope * 1000) / 10);
  const pair = s.currencyPair ?? 'your currency pair';
  const out: Prediction[] = [{
    id: `currency-trend-${improving ? 'up' : 'down'}`,
    kind: 'prediction',
    category: 'currency',
    eventType: 'PredictionCreated',
    title: `Currency exchange is ${improving ? 'improving' : 'weakening'}.`,
    detail: `${pair} has moved ${improving ? 'in your favor' : 'against you'} by ${pct}%.`,
    confidence: clampConfidence(55 + Math.min(25, pct * 5)),
    dataSources: ['currency'],
    supportingFactors: [`${pair} changed ${improving ? '+' : '-'}${pct}% over the tracked period.`],
    timeWindow: { start: now, end: now + 2 * DAY },
    expiresAt: now + 2 * DAY,
    createdAt: now,
  }];
  if (improving) {
    out.push({
      id: 'currency-exchange-today',
      kind: 'recommendation',
      category: 'currency',
      eventType: 'OpportunityDetected',
      title: 'Exchange money this afternoon.',
      detail: 'The rate is currently favorable — a good moment to exchange.',
      confidence: clampConfidence(50 + Math.min(25, pct * 5)),
      dataSources: ['currency'],
      supportingFactors: [`Exchange rate is up ${pct}% and currently favorable.`],
      timeWindow: { start: now, end: now + 12 * HOUR },
      expiresAt: now + 12 * HOUR,
      createdAt: now,
    });
  }
  return out;
};

// ─── Coworking ─────────────────────────────────────────────────────────────────

const coworkingCrowdPredictor: Predictor = (s, now) => {
  const crowded = (s.coworking ?? []).filter((c) => (c.expectedOccupancyPctTomorrow ?? 0) >= 80);
  if (crowded.length === 0) return [];
  const peak = crowded[0].peakStartHour ?? 10;
  return [
    {
      id: 'coworking-crowded-tomorrow',
      kind: 'prediction',
      category: 'coworking',
      eventType: 'PredictionCreated',
      title: 'Coworking spaces are expected to be crowded tomorrow.',
      detail: `${crowded.map((c) => c.name).join(', ')} ${crowded.length === 1 ? 'is' : 'are'} forecast above 80% occupancy.`,
      confidence: 70,
      dataSources: ['coworking', 'community'],
      supportingFactors: crowded.map((c) => `${c.name} is forecast at ${c.expectedOccupancyPctTomorrow}% occupancy.`),
      timeWindow: { start: now + DAY, end: now + 2 * DAY },
      expiresAt: now + 2 * DAY,
      createdAt: now,
    },
    {
      id: 'coworking-beat-peak',
      kind: 'recommendation',
      category: 'coworking',
      eventType: 'PredictionCreated',
      title: 'Visit coworking before peak hours.',
      detail: `Arrive before ${peak}:00 to get a seat.`,
      confidence: 68,
      dataSources: ['coworking'],
      supportingFactors: [`Spaces typically fill up around ${peak}:00.`, `${crowded[0].name} is forecast at ${crowded[0].expectedOccupancyPctTomorrow}% occupancy.`],
      timeWindow: { start: now + DAY, end: hourStart(now + DAY, peak) },
      expiresAt: hourStart(now + DAY, peak),
      createdAt: now,
    },
  ];
};

const newCoworkingPredictor: Predictor = (s, now) => {
  const fresh = (s.coworking ?? []).find((c) => c.openedDaysAgo != null && c.openedDaysAgo <= 30);
  if (!fresh) return [];
  return [{
    id: `coworking-new-${fresh.name}`,
    kind: 'opportunity',
    category: 'coworking',
    eventType: 'OpportunityDetected',
    title: 'New coworking opening.',
    detail: `${fresh.name} opened ${fresh.openedDaysAgo} days ago${fresh.internetMbps ? ` with ${fresh.internetMbps} Mbps internet` : ''}.`,
    confidence: 72,
    dataSources: ['coworking', 'community'],
    supportingFactors: [
      `${fresh.name} opened ${fresh.openedDaysAgo} days ago.`,
      'New spaces are typically uncrowded and offer intro pricing.',
    ],
    timeWindow: { start: now, end: now + 14 * DAY },
    expiresAt: now + 14 * DAY,
    createdAt: now,
  }];
};

// ─── Visa ──────────────────────────────────────────────────────────────────────

const visaPredictor: Predictor = (s, now) => {
  if (s.visaExpiresAt == null) return [];
  const days = Math.floor((s.visaExpiresAt - now) / DAY);
  if (days < 0 || days > 45) return [];
  return [{
    id: 'visa-expiry',
    kind: 'prediction',
    category: 'visa',
    eventType: days <= 14 ? 'RiskDetected' : 'TravelAlert',
    title: `Your visa expires in ${days} days.`,
    detail: days <= 14 ? 'Arrange an extension or onward travel urgently.' : 'Start planning your extension or next destination.',
    confidence: 99, // schedule-based, near-certain
    dataSources: ['calendar'],
    supportingFactors: [`Visa expiration date is ${new Date(s.visaExpiresAt).toISOString().slice(0, 10)}.`],
    timeWindow: { start: now, end: s.visaExpiresAt },
    expiresAt: s.visaExpiresAt,
    createdAt: now,
  }];
};

// ─── Events & community ────────────────────────────────────────────────────────

const eventsPredictor: Predictor = (s, now) => {
  const out: Prediction[] = [];
  const events = s.upcomingEvents ?? [];
  const sellingOut = events.filter((e) => (e.soldRatio ?? 0) >= 0.8 && e.startsAt > now && e.startsAt - now <= 5 * DAY);
  if (sellingOut.length > 0) {
    out.push({
      id: 'events-selling-out',
      kind: 'prediction',
      category: 'events',
      eventType: 'PredictionCreated',
      title: 'Weekend events are selling out.',
      detail: `${sellingOut.map((e) => e.title).join(', ')} — book soon if interested.`,
      confidence: 78,
      dataSources: ['events', 'community'],
      supportingFactors: sellingOut.map((e) => `${e.title} is ${Math.round((e.soldRatio ?? 0) * 100)}% sold.`),
      timeWindow: { start: now, end: Math.max(...sellingOut.map((e) => e.startsAt)) },
      expiresAt: Math.max(...sellingOut.map((e) => e.startsAt)),
      createdAt: now,
    });
  }
  const special = events.find((e) => e.priceUsd === 0 && e.startsAt > now && e.startsAt - now <= 7 * DAY && !e.isCommunityMeetup);
  if (special) {
    out.push({
      id: `events-special-${special.id}`,
      kind: 'opportunity',
      category: 'events',
      eventType: 'OpportunityDetected',
      title: 'Special local event.',
      detail: `${special.title} — free entry.`,
      confidence: 70,
      dataSources: ['events'],
      supportingFactors: [`${special.title} is free and starts ${new Date(special.startsAt).toISOString().slice(0, 10)}.`],
      timeWindow: { start: now, end: special.startsAt },
      expiresAt: special.startsAt,
      createdAt: now,
    });
  }
  const meetup = events.find((e) => e.isCommunityMeetup && e.startsAt - now > 0 && e.startsAt - now <= 36 * HOUR);
  if (meetup) {
    out.push({
      id: `community-meetup-${meetup.id}`,
      kind: 'prediction',
      category: 'community',
      eventType: 'PredictionCreated',
      title: 'Community meetup tomorrow.',
      detail: `${meetup.title} — meet other WorkRoamers nearby.`,
      confidence: 85,
      dataSources: ['community', 'events'],
      supportingFactors: [`${meetup.title} starts within 36 hours.`],
      timeWindow: { start: meetup.startsAt, end: meetup.startsAt + 4 * HOUR },
      expiresAt: meetup.startsAt + 4 * HOUR,
      createdAt: now,
    });
  }
  return out;
};

// ─── Traffic ───────────────────────────────────────────────────────────────────

const trafficPredictor: Predictor = (s, now) => {
  const byHour = s.trafficByHour;
  if (!byHour || byHour.length !== 24) return [];
  let peakHour = 0;
  for (let h = 1; h < 24; h++) if (byHour[h] > byHour[peakHour]) peakHour = h;
  if (byHour[peakHour] < 75) return [];
  const peakAt = hourStart(now, peakHour);
  const out: Prediction[] = [{
    id: `traffic-peak-${peakHour}`,
    kind: 'prediction',
    category: 'traffic',
    eventType: 'TravelAlert',
    title: `Traffic will be heavy at ${peakHour > 12 ? peakHour - 12 : peakHour} ${peakHour >= 12 ? 'PM' : 'AM'}.`,
    detail: 'Plan trips outside the congestion window.',
    confidence: clampConfidence(50 + byHour[peakHour] / 4),
    dataSources: ['transportation'],
    supportingFactors: [`Typical congestion reaches ${byHour[peakHour]}% at that hour.`],
    timeWindow: { start: peakAt - HOUR, end: peakAt + 2 * HOUR },
    expiresAt: peakAt + 2 * HOUR,
    createdAt: now,
  }];
  if (byHour[peakHour] >= 90 && s.weather && (s.weather.condition === 'storm' || s.weather.condition === 'rain')) {
    out.push({
      id: 'traffic-delay-travel',
      kind: 'recommendation',
      category: 'traffic',
      eventType: 'TravelAlert',
      title: 'Delay travel until tomorrow.',
      detail: 'Severe congestion plus bad weather — moving today will be slow.',
      confidence: 74,
      dataSources: ['transportation', 'weather'],
      supportingFactors: [
        `Congestion is forecast at ${byHour[peakHour]}%.`,
        `Weather condition is ${s.weather.condition}.`,
      ],
      timeWindow: { start: now, end: now + DAY },
      expiresAt: now + DAY,
      createdAt: now,
    });
  }
  return out;
};

// ─── Air quality & internet ────────────────────────────────────────────────────

const airQualityPredictor: Predictor = (s, now) => {
  const slope = trendSlope(s.airQualityHistory);
  if (slope == null || slope < 0.15) return []; // AQI rising = declining quality
  const latest = s.airQualityHistory![s.airQualityHistory!.length - 1].value;
  return [{
    id: 'air-quality-declining',
    kind: 'prediction',
    category: 'airQuality',
    eventType: 'RiskDetected',
    title: 'Air quality is expected to decline.',
    detail: 'Consider indoor work and limiting outdoor exercise.',
    confidence: clampConfidence(55 + Math.min(30, slope * 100)),
    dataSources: ['airQuality'],
    supportingFactors: [
      `AQI has risen ${Math.round(slope * 100)}% over the tracked period.`,
      `Current AQI is ${Math.round(latest)}.`,
    ],
    timeWindow: { start: now, end: now + 2 * DAY },
    expiresAt: now + 2 * DAY,
    createdAt: now,
  }];
};

const internetOutagePredictor: Predictor = (s, now) => {
  const recent = (s.internetOutages ?? []).filter((o) => now - o.reportedAt <= 24 * HOUR);
  if (recent.length < 2) return [];
  return [{
    id: 'internet-outages-nearby',
    kind: 'prediction',
    category: 'internet',
    eventType: 'RiskDetected',
    title: 'Internet outages have been reported nearby.',
    detail: 'Have a backup connection ready (hotspot, second workspace).',
    confidence: clampConfidence(45 + recent.length * 10),
    dataSources: ['internet', 'community'],
    supportingFactors: [`${recent.length} outage reports in the last 24 hours (${[...new Set(recent.map((o) => o.area))].join(', ')}).`],
    timeWindow: { start: now, end: now + DAY },
    expiresAt: now + DAY,
    createdAt: now,
  }];
};

const internetUpgradePredictor: Predictor = (s, now) => {
  const series = s.internetSpeedHistory;
  if (!series || series.length < 3) return [];
  const latest = series[series.length - 1].value;
  const prior = series.slice(0, -1);
  const avg = prior.reduce((sum, p) => sum + p.value, 0) / prior.length;
  if (avg <= 0 || latest < avg * 1.5) return [];
  return [{
    id: 'internet-upgrade',
    kind: 'opportunity',
    category: 'internet',
    eventType: 'OpportunityDetected',
    title: 'Temporary internet upgrade.',
    detail: `Speeds are running at ${Math.round(latest)} Mbps — well above the usual ${Math.round(avg)} Mbps.`,
    confidence: 70,
    dataSources: ['internet'],
    supportingFactors: [`Measured ${Math.round(latest)} Mbps vs a ${Math.round(avg)} Mbps average.`],
    timeWindow: { start: now, end: now + DAY },
    expiresAt: now + DAY,
    createdAt: now,
  }];
};

// ─── Neighborhood, flights, jobs, missions ─────────────────────────────────────

const neighborhoodCostPredictor: Predictor = (s, now) => {
  const out: Prediction[] = [];
  for (const n of s.neighborhoodCosts ?? []) {
    const slope = trendSlope(n.series);
    if (slope == null || slope < 0.1) continue;
    out.push({
      id: `neighborhood-cost-${n.neighborhood}`,
      kind: 'prediction',
      category: 'neighborhood',
      eventType: 'PredictionCreated',
      title: 'Neighborhood becoming more expensive.',
      detail: `${n.neighborhood} costs are up about ${Math.round(slope * 100)}%.`,
      confidence: clampConfidence(55 + Math.min(25, slope * 100)),
      dataSources: ['housing', 'community'],
      supportingFactors: [`${n.neighborhood} cost index rose ${Math.round(slope * 100)}% across ${n.series.length} data points.`],
      timeWindow: { start: now, end: now + 30 * DAY },
      expiresAt: now + 14 * DAY,
      createdAt: now,
    });
  }
  return out;
};

const flightPricePredictor: Predictor = (s, now) => {
  const slope = trendSlope(s.flightPrices);
  if (slope == null || slope > -0.1) return [];
  const pct = Math.abs(Math.round(slope * 100));
  return [{
    id: 'flight-price-drop',
    kind: 'opportunity',
    category: 'flights',
    eventType: 'PriceDropDetected',
    title: 'Flight prices decreased.',
    detail: `Fares on your tracked route are down about ${pct}%.`,
    confidence: clampConfidence(55 + Math.min(30, pct * 2)),
    dataSources: ['transportation'],
    supportingFactors: [`Tracked fares dropped ${pct}% over the recent period.`],
    timeWindow: { start: now, end: now + 3 * DAY },
    expiresAt: now + 3 * DAY,
    createdAt: now,
  }];
};

const jobDemandPredictor: Predictor = (s, now) => {
  const slope = trendSlope(s.jobDemandIndex);
  if (slope == null || slope < 0.15) return [];
  const pct = Math.round(slope * 100);
  return [{
    id: 'job-demand-up',
    kind: 'opportunity',
    category: 'jobs',
    eventType: 'OpportunityDetected',
    title: 'Remote job demand increased.',
    detail: `Demand in your field is up about ${pct}% — a good window to apply.`,
    confidence: clampConfidence(50 + Math.min(30, pct)),
    dataSources: ['jobs'],
    supportingFactors: [`Remote job demand index rose ${pct}% over the tracked period.`],
    timeWindow: { start: now, end: now + 14 * DAY },
    expiresAt: now + 14 * DAY,
    createdAt: now,
  }];
};

const missionPredictor: Predictor = (s, now) => {
  const urgent = (s.pendingMissions ?? []).filter(
    (m) => m.expiresAt != null && m.expiresAt > now && m.expiresAt - now <= 24 * HOUR,
  );
  if (urgent.length === 0) return [];
  const top = [...urgent].sort((a, b) => (b.payout ?? 0) - (a.payout ?? 0))[0];
  return [{
    id: `mission-today-${top.id}`,
    kind: 'recommendation',
    category: 'mission',
    eventType: 'MissionOpportunity',
    title: 'Complete this mission today.',
    detail: `${top.title} expires within 24 hours${top.payout != null ? ` ($${top.payout})` : ''}.`,
    confidence: 88,
    dataSources: ['missions'],
    supportingFactors: [
      `${top.title} expires ${new Date(top.expiresAt!).toISOString().slice(0, 16).replace('T', ' at ')}.`,
      ...(top.payout != null ? [`Payout is $${top.payout}.`] : []),
    ],
    timeWindow: { start: now, end: top.expiresAt! },
    expiresAt: top.expiresAt!,
    createdAt: now,
  }];
};

/** All predictors — the engine runs every one on each refresh. */
export const PREDICTORS: Predictor[] = [
  rainPredictor,
  housingTrendPredictor,
  cheaperApartmentPredictor,
  hotelDiscountPredictor,
  currencyPredictor,
  coworkingCrowdPredictor,
  newCoworkingPredictor,
  visaPredictor,
  eventsPredictor,
  trafficPredictor,
  airQualityPredictor,
  internetOutagePredictor,
  internetUpgradePredictor,
  neighborhoodCostPredictor,
  flightPricePredictor,
  jobDemandPredictor,
  missionPredictor,
];
